import { useState, useEffect } from "react";
import { Sparkles, TrendingUp, TrendingDown, Target, Award, AlertTriangle, Loader2, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";

interface SalesMetrics {
  leadsContacted: number;
  callsMade: number;
  emailsSent: number;
  dealsWon: number;
  dealsLost: number;
  pipelineValue: number;
  avgDealSize: number;
  conversionRate: number;
}

interface AISalesCoachProps {
  metrics?: SalesMetrics;
  userName?: string;
}

interface CoachingInsight {
  type: "strength" | "improvement" | "tip";
  title: string;
  description: string;
}

export function AISalesCoach({ metrics, userName = "there" }: AISalesCoachProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [insights, setInsights] = useState<CoachingInsight[]>([]);
  const [overallScore, setOverallScore] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { chat } = useAIAssistant();

  const analyzePerformance = async () => {
    setIsAnalyzing(true);

    // Generate insights based on metrics or use defaults
    const defaultMetrics: SalesMetrics = metrics || {
      leadsContacted: 45,
      callsMade: 28,
      emailsSent: 120,
      dealsWon: 5,
      dealsLost: 3,
      pipelineValue: 150000,
      avgDealSize: 25000,
      conversionRate: 62,
    };

    // Calculate performance score
    const callsPerLead = defaultMetrics.callsMade / Math.max(defaultMetrics.leadsContacted, 1);
    const winRate = (defaultMetrics.dealsWon / Math.max(defaultMetrics.dealsWon + defaultMetrics.dealsLost, 1)) * 100;
    
    const score = Math.min(100, Math.round(
      (winRate * 0.4) + 
      (Math.min(callsPerLead * 50, 30)) + 
      (Math.min(defaultMetrics.conversionRate * 0.3, 30))
    ));
    
    setOverallScore(score);

    // Generate coaching insights
    const newInsights: CoachingInsight[] = [];

    if (winRate >= 60) {
      newInsights.push({
        type: "strength",
        title: "Strong Closing Skills",
        description: `Your ${winRate.toFixed(0)}% win rate is excellent. Keep leveraging your negotiation techniques.`,
      });
    } else if (winRate < 40) {
      newInsights.push({
        type: "improvement",
        title: "Improve Qualification Process",
        description: "Consider spending more time qualifying leads upfront to improve your win rate.",
      });
    }

    if (callsPerLead < 0.5) {
      newInsights.push({
        type: "improvement",
        title: "Increase Call Activity",
        description: "Try to make more calls per lead. Personal contact significantly increases conversion.",
      });
    } else {
      newInsights.push({
        type: "strength",
        title: "Great Call Discipline",
        description: "You are maintaining good call activity. Keep up the momentum!",
      });
    }

    newInsights.push({
      type: "tip",
      title: "AI Tip of the Day",
      description: score >= 70 
        ? "Focus on upselling to existing customers. They are 60-70% more likely to convert than new prospects."
        : "Block 2 hours each morning for prospecting. Consistency in outreach leads to predictable results.",
    });

    setInsights(newInsights);
    setIsAnalyzing(false);
  };

  useEffect(() => {
    analyzePerformance();
  }, [metrics]);

  const getScoreColor = () => {
    if (overallScore >= 70) return "text-success";
    if (overallScore >= 50) return "text-warning";
    return "text-destructive";
  };

  const getScoreLabel = () => {
    if (overallScore >= 80) return "Excellent";
    if (overallScore >= 70) return "Great";
    if (overallScore >= 50) return "Good";
    if (overallScore >= 30) return "Needs Work";
    return "Getting Started";
  };

  const getInsightIcon = (type: CoachingInsight["type"]) => {
    switch (type) {
      case "strength":
        return Award;
      case "improvement":
        return AlertTriangle;
      case "tip":
        return Sparkles;
    }
  };

  const getInsightColor = (type: CoachingInsight["type"]) => {
    switch (type) {
      case "strength":
        return "text-success bg-success/10 border-success/30";
      case "improvement":
        return "text-warning bg-warning/10 border-warning/30";
      case "tip":
        return "text-primary bg-primary/10 border-primary/30";
    }
  };

  return (
    <div className="glass rounded-xl overflow-hidden animate-fade-in">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center">
            <Target className="h-5 w-5 text-accent" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground">AI Sales Coach</h3>
            <p className="text-xs text-muted-foreground">Personalized performance insights</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isAnalyzing && (
            <div className="flex items-center gap-2">
              <span className={cn("text-2xl font-bold", getScoreColor())}>{overallScore}</span>
              <span className="text-xs text-muted-foreground">/ 100</span>
            </div>
          )}
          <ChevronRight className={cn("h-5 w-5 text-muted-foreground transition-transform", isExpanded && "rotate-90")} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Performance Score */}
          <div className="p-4 rounded-lg bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Performance Score</span>
              <span className={cn("text-sm font-semibold", getScoreColor())}>{getScoreLabel()}</span>
            </div>
            <Progress value={overallScore} className="h-2" />
          </div>

          {/* Insights */}
          {isAnalyzing ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span>Analyzing your performance...</span>
            </div>
          ) : (
            <div className="space-y-3">
              {insights.map((insight, index) => {
                const Icon = getInsightIcon(insight.type);
                return (
                  <div
                    key={index}
                    className={cn(
                      "p-3 rounded-lg border transition-all animate-fade-in",
                      getInsightColor(insight.type)
                    )}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm">{insight.title}</p>
                        <p className="text-xs opacity-80 mt-0.5">{insight.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              analyzePerformance();
            }}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh Insights
          </Button>
        </div>
      )}
    </div>
  );
}
